import { useEffect, useState } from "react";
import { getThemePreference, setThemePreference, THEME_CHANGE_EVENT, type ThemePreference } from "../lib/theme";

const THEME_OPTIONS: { value: ThemePreference; label: string; hint: string }[] = [
  { value: "system", label: "Use system setting", hint: "Follows your device's light/dark setting automatically" },
  { value: "light", label: "Light", hint: "Always use the light appearance" },
  { value: "dark", label: "Dark", hint: "Always use the dark appearance" },
];

function ThemeIcon({ pref }: { pref: ThemePreference }) {
  if (pref === "light") {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="3.1" stroke="currentColor" strokeWidth="1.3" />
        <path
          d="M8 1.5v1.4M8 13.1v1.4M14.5 8h-1.4M2.9 8H1.5M12.6 3.4l-1 1M4.4 11.6l-1 1M12.6 12.6l-1-1M4.4 4.4l-1-1"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  if (pref === "dark") {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M13.5 9.9A5.6 5.6 0 0 1 6.1 2.5a5.6 5.6 0 1 0 7.4 7.4Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1.75" y="2.75" width="12.5" height="8" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.5 13.25h5M8 10.75v2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

/** Full-size theme picker for the Settings page — same three choices the
 * old rail popover offered, just laid out as a proper settings control
 * instead of a cramped dropdown. */
export function ThemePicker() {
  const [pref, setPref] = useState<ThemePreference>(() => getThemePreference());

  useEffect(() => {
    function onChange(e: Event) {
      setPref((e as CustomEvent<ThemePreference>).detail);
    }
    window.addEventListener(THEME_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(THEME_CHANGE_EVENT, onChange);
  }, []);

  return (
    <div className="theme-picker" role="radiogroup" aria-label="Theme">
      {THEME_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={pref === opt.value}
          className={pref === opt.value ? "theme-picker-option active" : "theme-picker-option"}
          onClick={() => setThemePreference(opt.value)}
        >
          <span className="theme-picker-icon">
            <ThemeIcon pref={opt.value} />
          </span>
          <span className="theme-picker-text">
            <span className="theme-picker-label">{opt.label}</span>
            <span className="theme-picker-hint">{opt.hint}</span>
          </span>
          <span className="theme-picker-radio" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
